/**
 * Canonical Lens content / intake budgets.
 *
 * This file is the single source of truth for the numeric limits Edge Functions
 * and the app use for document size, extraction size, and intake provenance
 * record caps. Change a value here, redeploy the functions that bundle it, and
 * ship the app — do **not** put these in Supabase secrets.
 *
 * Why not secrets: Supabase tokenizes secret values after set, so an opaque
 * live override (historically `LENS_MAX_ANALYSIS_WORDS` behaving like 6,000
 * while this file said 75,000) cannot be inspected or audited. Limits that
 * gate analysis completeness must be visible in git.
 *
 * Legacy secret names that must be **unset** after deploy (and are ignored if
 * still present): `LENS_MAX_ANALYSIS_WORDS`, `LENS_MAX_EXTRACTED_CHARS`,
 * `LENS_EVIDENCE_MAX_RECORDS`, `LENS_CANDIDATE_MAX_RECORDS`.
 *
 * App import: path alias `@lina/lens-limits` → this file (same pattern as
 * `@lina/extract`). Edge import: relative path into `_shared/limits/`.
 *
 * 2026-09-22: extended with every other hardcoded page/characteristic/evidence/
 * findings/etc. numeric limit found across the lens backend, so none of these
 * drift out of sync between the files that used to hardcode them locally.
 */

/** Product analysis word budget. Over = reject DOCUMENT_TOO_LONG (fail closed). */
export const LENS_MAX_ANALYSIS_WORDS = 75_000;

/** Max characters of extracted / crawled text before DOCUMENT_TOO_LONG. */
export const LENS_MAX_EXTRACTED_CHARS = 2_000_000;

/** Upload / file byte cap (50 MiB). */
export const LENS_MAX_FILE_BYTES = 50 * 1024 * 1024;

/** Intake provenance: max evidence records per assessment. */
export const LENS_EVIDENCE_MAX_RECORDS = 10_000;

/** Intake provenance: max structural candidate records per assessment. */
export const LENS_CANDIDATE_MAX_RECORDS = 5_000;

/**
 * Product website page quota (paid Lens path + free-audit crawl default).
 * `LENS_EXTRACT_MAX_URLS_PER_REQUEST` / `LENS_EXTRACT_MAX_PAGES` are pinned to
 * this value on purpose (2026-09-22) — they used to be independent literals
 * stuck at 10, which silently capped real requests below this quota once it
 * was raised past 10. Distinct from pilot crawl (`LENS_PILOT_WEBSITE_SCAN_MAX_PAGES`, 100).
 */
export const LENS_MAX_SITE_PAGES = 20;

/** Registry analyzes one canonical URL for each supported organization page type. */
export const REGISTRY_PAGE_TYPES = {
  home: ["/"] as const,
  about: ["/about", "/about-us", "/who-we-are"] as const,
  careers: ["/career", "/careers", "/jobs", "/join-us", "/work-with-us"] as const,
} as const;
export type RegistryPageType = keyof typeof REGISTRY_PAGE_TYPES;
export const REGISTRY_MAX_PAGES_PER_SELECTION = 10;

/** Conservative ranking for an organization's About and Careers sitemap URLs. */
export function registryPageMatchScore(type: "about" | "careers", raw: string): number {
  let segments: string[];
  try {
    segments = new URL(raw).pathname.toLowerCase().split("/").filter(Boolean);
  } catch {
    return 0;
  }
  const terms = type === "about"
    ? ["about", "about-us", "who-we-are", "our-company", "our-story", "company"]
    : ["career", "careers", "jobs", "join-us", "work-with-us", "opportunities"];
  const index = segments.findIndex((segment) => terms.includes(segment));
  if (index < 0 || segments.length > 4) return 0;
  if (
    type === "about" &&
    segments.some((segment) =>
      REGISTRY_PAGE_TYPES.careers.some((path) => path.slice(1) === segment)
    )
  ) return 0;
  if (
    segments.some((segment) => ["news", "blog", "events", "press", "article"].includes(segment))
  ) {
    return 0;
  }
  const exact = REGISTRY_PAGE_TYPES[type].some((path) => path.slice(1) === segments.join("/"));
  return (exact ? 100 : 75) - index * 12 - (segments.length - 1) * 8;
}

/** XLSX rows per sheet before extract fails closed (no silent row drop). */
export const LENS_MAX_XLSX_ROWS_PER_SHEET = 1_000;

/**
 * classify/v7-classify-pipeline input char cap (rejects raw `content` before
 * extraction/analysis even starts). Distinct from `LENS_MAX_EXTRACTED_CHARS`,
 * which gates already-extracted text later in the pipeline.
 */
export const LENS_MAX_CONTENT_CHARS = 200_000;

/**
 * Extract API: max URLs accepted in a single batch request (Zod `.max()` on
 * the request schema). Tied to `LENS_MAX_SITE_PAGES` so the request-level
 * ceiling can never sit below the product page quota again.
 */
export const LENS_EXTRACT_MAX_URLS_PER_REQUEST = LENS_MAX_SITE_PAGES;

/**
 * Extract API: max pages per site crawl a single request may ask for (Zod
 * `.max()`). Tied to `LENS_MAX_SITE_PAGES` for the same reason as
 * `LENS_EXTRACT_MAX_URLS_PER_REQUEST`. Distinct from
 * `LENS_PILOT_WEBSITE_SCAN_MAX_PAGES` (pilot crawl, a separate feature).
 */
export const LENS_EXTRACT_MAX_PAGES = LENS_MAX_SITE_PAGES;

/** Extract API: default page count when a request omits `maxPages`. */
export const LENS_EXTRACT_DEFAULT_PAGES = 3;

/**
 * Pilot document website-scan page cap. Distinct from `LENS_MAX_SITE_PAGES`
 * (paid product quota) and `LENS_EXTRACT_MAX_PAGES` (extract API request max).
 */
export const LENS_PILOT_WEBSITE_SCAN_MAX_PAGES = 100;

/** Reader-facing source preview length (debug/UI excerpt). Shared by the lens engine types and the extract module — was duplicated verbatim in both before. */
export const LENS_SOURCE_PREVIEW_MAX_CHARS = 200_000;

/** classify-content: max chars of a page's stated purpose passed to the classifier prompt. */
export const LENS_CLASSIFY_PURPOSE_MAX_CHARS = 750;

/** Finding title truncation length. */
export const LENS_FINDING_TITLE_MAX_CHARS = 120;

/** Max findings surfaced per content-measurement pass (slice/break threshold). */
export const LENS_MAX_FINDINGS = 10;

/** Max open questions surfaced per report. */
export const LENS_MAX_OPEN_QUESTIONS = 4;

/** Characteristic prompt digest caps: heading/section/bullet/emphasis counts fed into the characteristic prompt builder. */
export const LENS_MAX_DIGEST_HEADINGS = 60;
export const LENS_MAX_DIGEST_SECTIONS = 10;
export const LENS_MAX_DIGEST_BULLETS = 60;
export const LENS_MAX_DIGEST_EMPHASIS = 40;

/** classifyCorpus digest caps: a separate digest pass from the characteristic prompt builder above, same category of budget, different call site. */
export const LENS_CLASSIFY_DIGEST_HEADING_CAP = 40;
export const LENS_CLASSIFY_DIGEST_SECTION_CAP = 12;
export const LENS_CLASSIFY_SECTION_PREVIEW_CHARS = 180;

/** Evidence snippet / provenance byte budgets. */
export const LENS_EVIDENCE_SNIPPET_MAX_BYTES = 240;
export const LENS_PROVENANCE_SUMMARY_MAX_BYTES = 8192;
export const LENS_PROVENANCE_FULL_MAX_BYTES = 524288;

/** Extract reading-sample preview length. */
export const LENS_MAX_CONTENT_SAMPLE_CHARS = 40_000;

/** Debug endpoint truncation. Was duplicated verbatim across content-debug.ts and extract/debug.ts before. */
export const LENS_DEBUG_MAX_OUTLINE_BLOCKS = 300;
export const LENS_DEBUG_UNASSIGNED_CONTENT_MIN_WORDS = 20;
export const LENS_DEBUG_UNASSIGNED_CONTENT_MIN_RATIO = 0.08;

/** Free Lens v6 cost caps (USD), enforced by middleware rate limiting. */
export const LENS_V6_MONTHLY_COST_CAP_USD = 50;
export const LENS_V6_PER_ASSESSMENT_COST_CAP_USD = 2;

/** Worker retry / concurrency / retention knobs. */
export const LENS_AUDIT_WORKER_MAX_RETRIES = 3;
export const LENS_REGRESSION_WORKER_MAX_CONCURRENCY = 5;
export const LENS_DELIVERY_SWEEP_PRIOR_REPORT_MAX_AGE_DAYS = 30;
export const LENS_DELIVERY_SWEEP_MAX_RETRIES = 3;

/**
 * Max concurrent per-page analyze LLM/DB work for a single website scan
 * (`lens-v7-analyze`'s `/scans` route). Was hardcoded to 3 in
 * v7-analyze-pipeline.ts, pinned "to match paid MAX_SITE_PAGES" back when
 * that quota (`LENS_MAX_SITE_PAGES`) was itself 3 — never revisited when the
 * quota was raised to 20 (2026-09-17), so throughput silently stayed capped
 * at the old page count. Raised to 5 (2026-09-22): a moderate bump, not tied
 * 1:1 to `LENS_MAX_SITE_PAGES`, because unbounded concurrency here still
 * risks an unbounded Bedrock fan-out per scan (see the call site comment).
 * Matches `LENS_REGRESSION_WORKER_MAX_CONCURRENCY`'s value as a similar
 * "several-at-once, not all-at-once" LLM-fanout knob.
 */
export const LENS_WEBSITE_PAGE_ANALYZE_CONCURRENCY = 5;

/**
 * Max concurrent per-page finding-narrative LLM calls for a single website
 * scan's summarize stage (2026-09-22). Summarize used to make ONE LLM call
 * covering every finding across every page at once, whose required JSON
 * output (one findingNarratives entry per finding, site-wide) grows with
 * page count and could exceed the model's output token budget on larger
 * scans — the call then failed closed on truncation. Split into one bounded
 * site-wide call (readerImpactSummary/thingsDoneWell/findingImpacts — these
 * do NOT scale with page count) plus one call per page for findingNarratives
 * only (the part that does). This bounds how many of those per-page calls
 * run at once, for the same reason `LENS_WEBSITE_PAGE_ANALYZE_CONCURRENCY`
 * bounds analyze's per-page fan-out. Matches its value.
 */
export const LENS_WEBSITE_PAGE_SUMMARIZE_CONCURRENCY = 5;

/** V3 site-pattern promotion policy. Both values are persisted in plan telemetry. */
export const LENS_SITE_PATTERN_MIN_OCCURRENCE_PAGES = 2;
export const LENS_SITE_PATTERN_PREVALENCE_THRESHOLD = 0.5;
/** Total model output budget for the prose-only Lina Lens narrator. */
export const LENS_REPORT_NARRATION_MAX_TOKENS = 4_000;

/** Total model output budget for the Ask Lina semantic/safety boundary. */
export const LINA_ASK_SEMANTIC_SAFETY_MAX_TOKENS = 5_000;

/** Extraction parsing heuristics: minimum items/words for a run of text to be treated as a visual list, and minimum words for a section to count as real content. */
export const LENS_VISUAL_LIST_MIN_ITEMS = 3;
export const LENS_VISUAL_LIST_MAX_WORDS = 25;
export const LENS_MIN_SECTION_WORDS = 8;

/** Rewrite near-duplicate detection threshold (0-1 similarity ratio; at/above this, a rewrite is rejected as too close to the original). */
export const LENS_NEAR_IDENTICAL_REWRITE_THRESHOLD = 0.92;

/** Content-Authenticity-Index score band thresholds (0-100). */
export const LENS_CAI_STRONG_THRESHOLD = 90;
export const LENS_CAI_GOOD_THRESHOLD = 75;
export const LENS_CAI_OKAY_THRESHOLD = 60;
export const LENS_CAI_NEEDS_WORK_THRESHOLD = 40;

/** Legacy env keys that previously overrode the constants above. Never read for values. */
export const LEGACY_LIMIT_SECRET_KEYS = [
  "LENS_MAX_ANALYSIS_WORDS",
  "LENS_MAX_EXTRACTED_CHARS",
  "LENS_EVIDENCE_MAX_RECORDS",
  "LENS_CANDIDATE_MAX_RECORDS",
] as const;

let legacySecretWarned = false;

/**
 * If ops still have legacy limit secrets set, log once per isolate and ignore them.
 * Safe to call from any getter; no-op when Deno.env is unavailable (Node/app).
 */
export function warnIfLegacyLimitSecretsPresent(): void {
  if (legacySecretWarned) return;
  const env = (globalThis as { Deno?: { env?: { get?: (k: string) => string | undefined } } }).Deno
    ?.env;
  if (!env?.get) return;

  const present: string[] = [];
  for (const key of LEGACY_LIMIT_SECRET_KEYS) {
    if (env.get(key)) present.push(key);
  }
  if (present.length === 0) return;

  legacySecretWarned = true;
  console.warn(
    `[limits-registry] Ignoring legacy limit secret(s) ${present.join(", ")}; ` +
      `budgets come from _shared/limits/registry.ts. Unset these secrets after deploy.`,
  );
}

export function getLensMaxAnalysisWords(): number {
  warnIfLegacyLimitSecretsPresent();
  return LENS_MAX_ANALYSIS_WORDS;
}

export function getLensMaxExtractedChars(): number {
  warnIfLegacyLimitSecretsPresent();
  return LENS_MAX_EXTRACTED_CHARS;
}

export function getLensMaxFileBytes(): number {
  return LENS_MAX_FILE_BYTES;
}

export function getEvidenceMaxRecords(): number {
  warnIfLegacyLimitSecretsPresent();
  return LENS_EVIDENCE_MAX_RECORDS;
}

export function getCandidateMaxRecords(): number {
  warnIfLegacyLimitSecretsPresent();
  return LENS_CANDIDATE_MAX_RECORDS;
}

export function getLensMaxSitePages(): number {
  return LENS_MAX_SITE_PAGES;
}

export function getLensMaxXlsxRowsPerSheet(): number {
  return LENS_MAX_XLSX_ROWS_PER_SHEET;
}

export function getLensMaxContentChars(): number {
  return LENS_MAX_CONTENT_CHARS;
}

export function getLensExtractMaxUrlsPerRequest(): number {
  return LENS_EXTRACT_MAX_URLS_PER_REQUEST;
}

export function getLensExtractMaxPages(): number {
  return LENS_EXTRACT_MAX_PAGES;
}

export function getLensExtractDefaultPages(): number {
  return LENS_EXTRACT_DEFAULT_PAGES;
}

export function getLensPilotWebsiteScanMaxPages(): number {
  return LENS_PILOT_WEBSITE_SCAN_MAX_PAGES;
}

export function getLensSourcePreviewMaxChars(): number {
  return LENS_SOURCE_PREVIEW_MAX_CHARS;
}

export function getLensClassifyPurposeMaxChars(): number {
  return LENS_CLASSIFY_PURPOSE_MAX_CHARS;
}

export function getLensFindingTitleMaxChars(): number {
  return LENS_FINDING_TITLE_MAX_CHARS;
}

export function getLensMaxFindings(): number {
  return LENS_MAX_FINDINGS;
}

export function getLensMaxOpenQuestions(): number {
  return LENS_MAX_OPEN_QUESTIONS;
}

export function getLensMaxDigestHeadings(): number {
  return LENS_MAX_DIGEST_HEADINGS;
}

export function getLensMaxDigestSections(): number {
  return LENS_MAX_DIGEST_SECTIONS;
}

export function getLensMaxDigestBullets(): number {
  return LENS_MAX_DIGEST_BULLETS;
}

export function getLensMaxDigestEmphasis(): number {
  return LENS_MAX_DIGEST_EMPHASIS;
}

export function getLensClassifyDigestHeadingCap(): number {
  return LENS_CLASSIFY_DIGEST_HEADING_CAP;
}

export function getLensClassifyDigestSectionCap(): number {
  return LENS_CLASSIFY_DIGEST_SECTION_CAP;
}

export function getLensClassifySectionPreviewChars(): number {
  return LENS_CLASSIFY_SECTION_PREVIEW_CHARS;
}

export function getLensEvidenceSnippetMaxBytes(): number {
  return LENS_EVIDENCE_SNIPPET_MAX_BYTES;
}

export function getLensProvenanceSummaryMaxBytes(): number {
  return LENS_PROVENANCE_SUMMARY_MAX_BYTES;
}

export function getLensProvenanceFullMaxBytes(): number {
  return LENS_PROVENANCE_FULL_MAX_BYTES;
}

export function getLensMaxContentSampleChars(): number {
  return LENS_MAX_CONTENT_SAMPLE_CHARS;
}

export function getLensDebugMaxOutlineBlocks(): number {
  return LENS_DEBUG_MAX_OUTLINE_BLOCKS;
}

export function getLensDebugUnassignedContentMinWords(): number {
  return LENS_DEBUG_UNASSIGNED_CONTENT_MIN_WORDS;
}

export function getLensDebugUnassignedContentMinRatio(): number {
  return LENS_DEBUG_UNASSIGNED_CONTENT_MIN_RATIO;
}

export function getLensV6MonthlyCostCapUsd(): number {
  return LENS_V6_MONTHLY_COST_CAP_USD;
}

export function getLensV6PerAssessmentCostCapUsd(): number {
  return LENS_V6_PER_ASSESSMENT_COST_CAP_USD;
}

export function getLensAuditWorkerMaxRetries(): number {
  return LENS_AUDIT_WORKER_MAX_RETRIES;
}

export function getLensRegressionWorkerMaxConcurrency(): number {
  return LENS_REGRESSION_WORKER_MAX_CONCURRENCY;
}

export function getLensWebsitePageAnalyzeConcurrency(): number {
  return LENS_WEBSITE_PAGE_ANALYZE_CONCURRENCY;
}

export function getLensWebsitePageSummarizeConcurrency(): number {
  return LENS_WEBSITE_PAGE_SUMMARIZE_CONCURRENCY;
}

export function getLensSitePatternPolicy(): {
  minOccurrencePages: number;
  prevalenceThreshold: number;
} {
  return {
    minOccurrencePages: LENS_SITE_PATTERN_MIN_OCCURRENCE_PAGES,
    prevalenceThreshold: LENS_SITE_PATTERN_PREVALENCE_THRESHOLD,
  };
}

export function getLensReportNarrationMaxTokens(): number {
  return LENS_REPORT_NARRATION_MAX_TOKENS;
}

export function getLinaAskSemanticSafetyMaxTokens(): number {
  return LINA_ASK_SEMANTIC_SAFETY_MAX_TOKENS;
}

export function getLensDeliverySweepPriorReportMaxAgeDays(): number {
  return LENS_DELIVERY_SWEEP_PRIOR_REPORT_MAX_AGE_DAYS;
}

export function getLensDeliverySweepMaxRetries(): number {
  return LENS_DELIVERY_SWEEP_MAX_RETRIES;
}

export function getLensVisualListMinItems(): number {
  return LENS_VISUAL_LIST_MIN_ITEMS;
}

export function getLensVisualListMaxWords(): number {
  return LENS_VISUAL_LIST_MAX_WORDS;
}

export function getLensMinSectionWords(): number {
  return LENS_MIN_SECTION_WORDS;
}

export function getLensNearIdenticalRewriteThreshold(): number {
  return LENS_NEAR_IDENTICAL_REWRITE_THRESHOLD;
}

export function getLensCaiStrongThreshold(): number {
  return LENS_CAI_STRONG_THRESHOLD;
}

export function getLensCaiGoodThreshold(): number {
  return LENS_CAI_GOOD_THRESHOLD;
}

export function getLensCaiOkayThreshold(): number {
  return LENS_CAI_OKAY_THRESHOLD;
}

export function getLensCaiNeedsWorkThreshold(): number {
  return LENS_CAI_NEEDS_WORK_THRESHOLD;
}
